import React from 'react';
import { Users, Clock, ArrowRight, CheckCircle, MoreHorizontal } from 'lucide-react';

const IsometricCalendar = () => {
    // Standard Isometric rotation
    const sceneRotation = {
        transform: 'rotateX(55deg) rotateZ(45deg)',
        transformStyle: 'preserve-3d' as const
    };

    return (
        <div className="w-full h-full flex items-center justify-center scale-75 md:scale-100">
            <div
                className="relative w-[320px] h-[320px]"
                style={sceneRotation}
            >
                <style>
                    {`
                        @keyframes move-card {
                            0% { left: 125px; top: 190px; transform: translateZ(2px); }
                            100% { left: 230px; top: 190px; transform: translateZ(2px); }
                        }
                    `}
                </style>
                {/* Main Base Layer */}
                <div
                    className="absolute top-0 left-0 w-full h-full"
                    style={{
                        transform: 'translateZ(0px)',
                        transformStyle: 'preserve-3d'
                    }}
                >
                    {/* Shadow */}
                    <div
                        className="absolute top-0 left-0 w-full h-full rounded-3xl bg-indigo-900/40 blur-2xl"
                        style={{
                            transform: 'translateZ(-60px) translateY(40px) translateX(40px)',
                        }}
                    />

                    {/* Thickness Slices (Blue Base) */}
                    {Array.from({ length: 35 }).map((_, i) => (
                        <div
                            key={i}
                            className="absolute top-0 left-0 w-full h-full rounded-3xl bg-indigo-700"
                            style={{
                                transform: `translateZ(-${i + 1}px)`,
                            }}
                        />
                    ))}

                    {/* Top Face */}
                    <div
                        className="absolute top-0 left-0 w-full h-full rounded-3xl bg-indigo-600"
                        style={{
                            transform: 'translateZ(0px)',
                            boxShadow: 'inset 0 0 0 2px rgba(255,255,255,0.1)'
                        }}
                    >
                        {/* Subtle surface detail */}
                        <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent rounded-3xl" />

                        {/* Swimlane Dividers */}
                        <div className="absolute inset-0 flex">
                            <div className="flex-1 border-r border-indigo-500/50 bg-indigo-900/10 rounded-l-3xl"></div>
                            <div className="flex-1 border-r border-indigo-500/50"></div>
                            <div className="flex-1 bg-indigo-900/10 rounded-r-3xl"></div>
                        </div>
                    </div>
                </div>

                {/* Swimlane Headers (Flat on board) */}
                {['Dr. Smith', 'Dr. Jones', 'Techs'].map((name, i) => (
                    <div
                        key={i}
                        className="absolute top-4 h-8 flex items-center justify-center"
                        style={{
                            left: `${i * 33.33}%`,
                            width: '33.33%',
                            transform: 'translateZ(1px)'
                        }}
                    >
                        <div className="bg-indigo-800/50 px-3 py-1 rounded-full text-[10px] text-indigo-200 font-bold uppercase tracking-wider border border-indigo-500/30">
                            {name}
                        </div>
                    </div>
                ))}

                {/* Kanban Cards / Appointments */}

                {/* Card 1: Column 1 (Left) */}
                <div
                    className="absolute"
                    style={{
                        left: '20px',
                        top: '60px',
                        width: '70px',
                        height: '90px',
                        transform: 'translateZ(2px)',
                        transformStyle: 'preserve-3d'
                    }}
                >
                    {/* Thickness */}
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="absolute inset-0 rounded-xl bg-blue-800" style={{ transform: `translateZ(-${i}px)` }} />
                    ))}
                    <div className="absolute inset-0 bg-blue-500 rounded-xl p-2 border border-blue-400 shadow-sm flex flex-col gap-2">
                        <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                            <Users className="w-4 h-4 text-white" />
                        </div>
                        <div className="w-full h-2 bg-white/20 rounded"></div>
                        <div className="w-2/3 h-2 bg-white/20 rounded"></div>
                    </div>
                </div>

                {/* Card 2: Column 1 (Left) - Lower */}
                <div
                    className="absolute"
                    style={{
                        left: '20px',
                        top: '170px',
                        width: '70px',
                        height: '60px',
                        transform: 'translateZ(2px)',
                        transformStyle: 'preserve-3d'
                    }}
                >
                    {/* Thickness */}
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="absolute inset-0 rounded-xl bg-teal-800" style={{ transform: `translateZ(-${i}px)` }} />
                    ))}
                    <div className="absolute inset-0 bg-teal-500 rounded-xl p-2 border border-teal-400 shadow-sm">
                        <div className="w-full h-2 bg-white/20 rounded mb-2"></div>
                        <div className="w-1/2 h-2 bg-white/20 rounded"></div>
                    </div>
                </div>

                {/* Card 3: Column 2 (Center) */}
                <div
                    className="absolute"
                    style={{
                        left: '125px',
                        top: '100px',
                        width: '70px',
                        height: '80px',
                        transform: 'translateZ(2px)',
                        transformStyle: 'preserve-3d'
                    }}
                >
                    {/* Thickness */}
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="absolute inset-0 rounded-xl bg-orange-800" style={{ transform: `translateZ(-${i}px)` }} />
                    ))}
                    <div className="absolute inset-0 bg-orange-500 rounded-xl p-2 border border-orange-400 shadow-sm flex flex-col gap-2">
                        <div className="w-full h-2 bg-white/20 rounded"></div>
                        <div className="w-full h-2 bg-white/20 rounded"></div>
                        <div className="mt-auto flex justify-end">
                            <div className="w-4 h-4 rounded-full bg-white/30"></div>
                        </div>
                    </div>
                </div>

                {/* Card 4: Column 3 (Right) - Completed */}
                <div
                    className="absolute"
                    style={{
                        right: '20px',
                        top: '80px',
                        width: '70px',
                        height: '70px',
                        transform: 'translateZ(2px)',
                        transformStyle: 'preserve-3d'
                    }}
                >
                    {/* Thickness */}
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="absolute inset-0 rounded-xl bg-green-800" style={{ transform: `translateZ(-${i}px)` }} />
                    ))}
                    <div className="absolute inset-0 bg-green-500 rounded-xl flex items-center justify-center border border-green-400 shadow-sm">
                        <CheckCircle className="w-8 h-8 text-white/80" />
                    </div>
                </div>


                {/* Floating Elements - The "More than a calendar" aspect */}

                {/* Dragging Card (Moving from Col 2 to Col 3) */}
                <div
                    className="absolute"
                    style={{
                        left: '125px',
                        top: '190px',
                        width: '70px',
                        height: '80px',
                        transform: 'translateZ(2px)',
                        transformStyle: 'preserve-3d',
                        animation: 'move-card 2s ease-out forwards',
                        animationDelay: '1s'
                    }}
                >
                    {/* Shadow on board */}
                    <div
                        className="absolute inset-0 bg-black/20 blur-lg rounded-xl"
                        style={{ transform: 'translateZ(-50px) translateX(-10px) translateY(10px)' }}
                    />

                    {/* Thickness */}
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="absolute inset-0 rounded-xl bg-purple-800" style={{ transform: `translateZ(-${i}px)` }} />
                    ))}
                    <div className="absolute inset-0 bg-purple-600 rounded-xl p-2 border border-purple-400 shadow-xl flex flex-col items-center justify-center">
                        <Clock className="w-8 h-8 text-white mb-1" />
                        <div className="w-10 h-1.5 bg-white/30 rounded-full"></div>
                    </div>
                </div>

                {/* User Avatar Bubble (Floating) */}
                <div
                    className="absolute"
                    style={{
                        left: '190px',
                        top: '200px',
                        width: '20px',
                        height: '20px',
                        transform: 'translateZ(60px) rotateZ(5deg)', // Floating high and tilted
                        transformStyle: 'preserve-3d'
                    }}
                >
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="absolute inset-0 rounded-full bg-pink-700" style={{ transform: `translateZ(-${i}px)` }} />
                    ))}
                    <div className="absolute inset-0 bg-pink-500 rounded-full flex items-center justify-center shadow-lg border border-pink-300">
                        <div className="text-white font-bold text-lg">JD</div>
                    </div>
                </div>



            </div>
        </div>
    );
};

export default IsometricCalendar;
